import React, { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronRight, MapPin, Globe, ArrowLeft } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";

interface LocationSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (location: { continent: string; country: string; city: string }) => void;
  availableLocations: { country: string; continent: string; cities: string[] }[];
}

const CONTINENTS = [
  { id: "america", name: "América", emoji: "🌎" },
  { id: "europe", name: "Europa", emoji: "🌍" },
  { id: "asia", name: "Asia", emoji: "🌏" },
  { id: "africa", name: "África", emoji: "🌍" },
  { id: "oceania", name: "Oceanía", emoji: "🌏" },
];

const COUNTRY_FLAGS: Record<string, string> = {
  "españa": "🇪🇸",
  "colombia": "🇨🇴",
  "colómbia": "🇨🇴",
  "colombia ": "🇨🇴",
  "méxico": "🇲🇽",
  "mexico": "🇲🇽",
  "argentina": "🇦🇷",
  "chile": "🇨🇱",
  "perú": "🇵🇪",
  "peru": "🇵🇪",
  "eeuu": "🇺🇸",
  "estados unidos": "🇺🇸",
  "usa": "🇺🇸",
  "united states": "🇺🇸",
  "united state": "🇺🇸",
  "ee.uu": "🇺🇸",
  "canadá": "🇨🇦",
  "canada": "🇨🇦",
  "brasil": "🇧🇷",
  "venezuela": "🇻🇪",
  "ecuador": "🇪🇨",
  "bolivia": "🇧🇴",
  "uruguay": "🇺🇾",
  "paraguay": "🇵🇾",
  "guatemala": "🇬🇹",
  "costa rica": "🇨🇷",
  "panamá": "🇵🇦",
  "panama": "🇵🇦",
  "puerto rico": "🇵🇷",
  "república dominicana": "🇩🇴",
  "republica dominicana": "🇩🇴",
  "cuba": "🇨🇺",
  "francia": "🇫🇷",
  "italia": "🇮🇹",
  "alemania": "🇩🇪",
  "reino unido": "🇬🇧",
  "portugal": "🇵🇹",
  "países bajos": "🇳🇱",
  "suiza": "🇨🇭",
};

const getCountryFlag = (country: string) => {
  return COUNTRY_FLAGS[country.toLowerCase().trim()] || "🏳️";
};

export function LocationSelector({ isOpen, onClose, onSelect, availableLocations }: LocationSelectorProps) {
  const [step, setStep] = useState<"continent" | "country" | "city">("continent");
  const [selectedContinent, setSelectedContinent] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");

  const filteredCountries = availableLocations.filter(loc => 
    loc.continent.toLowerCase() === selectedContinent.toLowerCase()
  );

  const filteredCities = availableLocations.find(loc => 
    loc.country === selectedCountry
  )?.cities || [];

  const handleSelectContinent = (id: string) => {
    setSelectedContinent(id);
    setStep("country");
  };

  const handleSelectCountry = (country: string) => {
    setSelectedCountry(country);
    setStep("city");
  };

  const handleSelectCity = (city: string) => {
    onSelect({ continent: selectedContinent, country: selectedCountry, city });
    onClose();
  };

  const reset = () => {
    setStep("continent");
    setSelectedContinent("");
    setSelectedCountry("");
  };

  const availableContinents = CONTINENTS.filter(c => 
    availableLocations.some(loc => loc.continent.toLowerCase() === c.name.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] border-none shadow-2xl bg-background/95 backdrop-blur-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <MapPin className="h-6 w-6 text-primary" />
            ¿Desde dónde quieres explorar?
          </DialogTitle>
          <DialogDescription>
            Selecciona tu ubicación para mostrarte las mejores tiendas cerca de ti.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {step !== "continent" && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="mb-4 gap-1 p-0 hover:bg-transparent" 
              onClick={() => {
                if (step === "city") setStep("country");
                else if (step === "country") setStep("continent");
              }}
            >
              <ArrowLeft className="h-4 w-4" /> Volver
            </Button>
          )}

          <ScrollArea className="h-[300px] pr-4">
            {step === "continent" && (
              <div className="grid grid-cols-1 gap-3">
                {availableContinents.map((c) => (
                  <Button
                    key={c.id}
                    variant="outline"
                    className="h-16 justify-between text-lg hover:border-primary hover:bg-primary/5 transition-all group"
                    onClick={() => handleSelectContinent(c.name)}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-2xl">{c.emoji}</span>
                      {c.name}
                    </span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                  </Button>
                ))}
              </div>
            )}

            {step === "country" && (
              <div className="grid grid-cols-2 gap-3">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((loc) => (
                    <Button
                      key={loc.country}
                      variant="outline"
                      className="h-20 flex-col gap-2 hover:border-primary hover:bg-primary/5 transition-all"
                      onClick={() => handleSelectCountry(loc.country)}
                    >
                      <span className="text-3xl">{getCountryFlag(loc.country)}</span>
                      <span className="font-medium">{loc.country}</span>
                    </Button>
                  ))
                ) : (
                  <div className="col-span-2 text-center py-12">
                    <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
                    <p className="text-muted-foreground">No hay tiendas registradas en este continente aún.</p>
                  </div>
                )}
              </div>
            )}

            {step === "city" && (
              <div className="grid grid-cols-1 gap-2">
                <Button
                  variant="secondary"
                  className="h-12 justify-start font-bold border-2 border-primary/20"
                  onClick={() => handleSelectCity("Todas las ciudades")}
                >
                  📍 Todas las ciudades de {selectedCountry}
                </Button>
                {filteredCities.map((city) => (
                  <Button
                    key={city}
                    variant="outline"
                    className="h-12 justify-start hover:border-primary hover:bg-primary/5"
                    onClick={() => handleSelectCity(city)}
                  >
                    {city}
                  </Button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex gap-1">
            <Badge variant={step === "continent" ? "default" : "outline"} className="w-2 h-2 rounded-full p-0" />
            <Badge variant={step === "country" ? "default" : "outline"} className="w-2 h-2 rounded-full p-0" />
            <Badge variant={step === "city" ? "default" : "outline"} className="w-2 h-2 rounded-full p-0" />
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Saltar por ahora
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
